/**
 * The front page for someone who is not signed in (asked for 2026-09-27, started 2026-09-29) -
 * and, at /welcome, for someone who is (2026-10-11), with the way to their cards instead.
 * Before, the address went straight to a bare sign-in form - unfriendly, and the very thing
 * that looks like phishing to Google's Safe Browsing.
 *
 * Made simpler 2026-10-17, as the owner asked: ONE way in (no "My cards" in the corner as
 * well, no second button at the bottom); how it works said in three words-long steps under
 * the opening line rather than a section of its own; and the video up top, beside the
 * words, so it is seen at first glance. Its cover is the front page's picture: Eva's real
 * card - her photo with her dog in the big circle - on a party background (made by the
 * video studio, apps/web/video.html?poster; scripts/make-video.mjs poster). The video
 * itself shows the card made start to finish, with music (scripts/make-music.py).
 */
import { useRef, useState } from 'react';
import video from '../how-it-works.mp4';
import poster from '../how-it-works-poster.webp';
import { useSession } from '../auth-client.ts';
import { Logo } from '../logo.tsx';
import { Link } from '../router.tsx';
import { t } from '../strings.ts';
import { ThemeSwitch } from '../theme.tsx';
import { useSite } from '../idle.tsx';
import { SiteCode } from '../site-code.tsx';

/** The video, its cover the front page's picture; a click anywhere on it plays it. */
function HeroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const play = () => {
    setStarted(true);
    void ref.current?.play().catch(() => { /* the browser's own controls take over */ });
  };
  return (
    <figure className="landing-video">
      {/* only fetched when played: about 10 MB */}
      <video ref={ref} controls={started} playsInline preload="none" poster={poster} width={1280} height={720}
        aria-label={t('landingArtLabel')} onPlay={() => setStarted(true)}>
        <source src={video} type="video/mp4" />
      </video>
      {!started && (
        <button className="play-cover" onClick={play} aria-label={t('landingPlay')}>
          <span className="play-pill">
            <span className="play-disc" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M8 5.5v13l10.5-6.5z" /></svg></span>
            <span className="play-words"><strong>{t('landingPlay')}</strong><span>{t('landingPlayLength')}</span></span>
          </span>
        </button>
      )}
    </figure>
  );
}

export function LandingPage() {
  const { data: session } = useSession();
  const site = useSite();
  return (
    <main className="landing">
      <header className="landing-bar">
        <div className="brand"><Logo /></div>
        <div className="right">
          <ThemeSwitch compact />
          {!session && <Link href="/signin" className="button">{t('signIn')}</Link>}
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-copy">
          <h1>{t('landingTitle')}</h1>
          <p className="landing-lead">{t('landingLead')}</p>
          <ol className="landing-mini">
            <li><span className="step-num">1</span>{t('landingMini1')}</li>
            <li><span className="step-num">2</span>{t('landingMini2')}</li>
            <li><span className="step-num">3</span>{t('landingMini3')}</li>
          </ol>
          {session ? (
            <Link href="/" className="button primary big">{t('myCards')}</Link>
          ) : (
            <>
              <Link href="/signup" className="button primary big">{t('landingStart')}</Link>
              <p className="muted small landing-invite">{t(site?.approvalRequired ? 'landingInvite' : 'landingOpen')}</p>
              <SiteCode />
            </>
          )}
        </div>
        <HeroVideo />
      </section>

      <section className="landing-points">
        <div><h3>{t('landingPrivateTitle')}</h3><p>{t('landingPrivate')}</p></div>
        <div><h3>{t('landingCartoonTitle')}</h3><p>{t('landingCartoon')}</p></div>
        <div><h3>{t('landingPaperTitle')}</h3><p>{t('landingPaper')}</p></div>
        <div><h3>{t('landingDesignsTitle')}</h3><p>{t('landingDesigns')}</p></div>
      </section>
    </main>
  );
}
